import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingModel } from '../loading.model';
import { inject, Injectable } from '@angular/core';
import { GetUser, LoadProfilePicture, LoadUserProfilePicture, SaveUser } from './user.actions';
import { patch } from '@ngxs/store/operators';
import { of, tap } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { UserService } from '../../services/user/user.service';
import { StorageService } from '../../../services/storage.service';
import { BroadcastService } from '../../../services/broadcast.service';
import { ProfilePicture } from '../../models/user/profile-picture.model';
import { User } from 'src/app/models/user';

export type UserStateModel = LoadingModel & {
  profilePicture?: ProfilePicture,
  user: User,
};

export const initialState: UserStateModel = {
  isLoading: false,
  profilePicture: undefined,
  user: undefined,
};

@State<UserStateModel>({
  name: 'user',
  defaults: initialState,
})
@Injectable()
export class UserState {
  private readonly userService = inject(UserService);
  private readonly storageService = inject(StorageService);
  private readonly broadcastService = inject(BroadcastService);

  @Selector()
  static profilePicture(state: UserStateModel) {
    return state.profilePicture;
  }

  @Selector()
  static user(state: UserStateModel) {
    return state.user;
  }

  @Action(LoadUserProfilePicture)
  loadUserProfilePicture(context: StateContext<UserStateModel>, { user }: LoadUserProfilePicture) {
    if (!user.profilePictureUrl) {
      return;
    }

    return this.userService.getUserProfilePicture(user.profilePictureUrl).pipe(
      tap((data) => {
        const ext = user.profilePictureUrl.split('.').pop().split('?').shift();
        const reader = new FileReader();

        reader.onload = () => {
          const dataUrl = reader.result as string;
          const imageData = dataUrl.replace('data:application/octet-stream;', `data:image/${ext};`);

          this.storageService.set('profilePicture', imageData);
          this.broadcastService.broadcastProfilePicture(true);
        };

        reader.readAsDataURL(data);
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false })
        );
      }),
    )
  }

  @Action(LoadProfilePicture)
  loadProfilePicture(context: StateContext<UserStateModel>, { userId }: LoadProfilePicture) {
    context.setState(
      patch({ isLoading: true })
    );

    return this.userService.getProfilePicture(userId).pipe(
      tap((profilePicture) => {
        context.setState(patch({
          profilePicture,
        }))
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false })
        );
      }),
    )
  }

  @Action(SaveUser)
  saveUser(context: StateContext<UserStateModel>, { user }: SaveUser) {
    localStorage.setItem('userId', user.id);

    // This will be useless until we remove the refresh after login
    context.setState(
      patch({ user }),
    );
  }

  @Action(GetUser)
  getUser(context: StateContext<UserStateModel>, { userId }: GetUser) {
    return this.userService.getUser(userId).pipe(tap((user) => {
      context.setState(patch({ user }));
    }));
  }
}
